import { DeleteOutlined } from '@ant-design/icons';
import {
  Form,
  FormInstance,
  FormListFieldData,
  FormListOperation,
  Input,
  Select,
  Space,
} from 'antd';
import { InfinitySelectProps } from 'components/form/InfinitySelect';
import { OPERATORS, CONDITIONS } from 'constants/filters';
import { get } from 'lodash';
import ValueForm from './ValueForm';
import { ReactElement, useEffect } from 'react';

export type TFilterItem = {
  key: string;
  dataSource?: { value: string | number | boolean; label: string }[];
  label?: string;
  reference?: InfinitySelectProps;
  valueCPN?: ReactElement;
  isDatePicker?: boolean;
  isCreator?: boolean;
  isCheckNull?: boolean;
  isArraySelect?: boolean;
  operators?: { value?: string; label?: string }[];
};

type TFilterRowProps = Omit<FormListOperation, 'add' | 'move'> & {
  filters?: TFilterItem[];
  hiddenRemove?: boolean;
  form?: FormInstance;
  subFields?: FormListFieldData[];
  subField?: FormListFieldData;
  indexParent?: number;
};

const FilterRow = ({
  subField,
  subFields,
  filters,
  remove,
  hiddenRemove,
  form,
  indexParent,
  ...props
}: TFilterRowProps) => {
  const key = Form.useWatch(
    ['filters', indexParent, 'value', subField?.name, 'key'],
    form,
  );

  const filter = filters.find((e) => e.key === key);

  useEffect(() => {
    if (filter?.isDatePicker) {
      form.setFieldValue(
        ['filters', indexParent, 'value', subField?.name, 'operator'],
        'between',
      );
    } else if (filter?.isCreator) {
      form.setFieldValue(
        ['filters', indexParent, 'value', subField?.name, 'operator'],
        'contL',
      );
    } else if (filter?.isCheckNull) {
      form.setFieldValue(
        ['filters', indexParent, 'value', subField?.name, 'operator'],
        'is',
      );
    }
  }, [filter]);

  return (
    <Space style={{ display: 'flex', marginBottom: 8 }} align="baseline">
      <div>
        {subFields.length <= 1 ? (
          <></>
        ) : (
          <>
            {subField.name === 0 ? (
              <Form.Item
                shouldUpdate={(o, n) => {
                  return (
                    get(o, ['filters'])?.[indexParent]?.value?.[1]
                      ?.condition !==
                    get(n, ['filters'])?.[indexParent]?.value?.[1]?.condition
                  );
                }}
              >
                {({ getFieldValue, setFieldValue }) => {
                  const currentFilters = getFieldValue('filters');
                  setFieldValue(
                    ['filters', indexParent, 'value', 0, 'condition'],
                    currentFilters?.[indexParent]?.value?.[1]?.condition,
                  );

                  return (
                    <Form.Item
                      name={[subField.name, 'condition']}
                      rules={[
                        {
                          required: true,
                          message: 'Missing Root condition',
                        },
                      ]}
                      className="w-[80px]"
                    >
                      Where
                    </Form.Item>
                  );
                }}
              </Form.Item>
            ) : (
              <>
                {subField.name === 1 ? (
                  <Form.Item
                    name={[subField.name, 'condition']}
                    rules={[
                      { required: true, message: 'Missing Root condition' },
                    ]}
                    className="w-[80px]"
                  >
                    <Select options={CONDITIONS} placeholder="Data" />
                  </Form.Item>
                ) : (
                  <Form.Item
                    shouldUpdate={(o, n) => {
                      return (
                        get(o, ['filters'])?.[indexParent]?.value?.[1]
                          ?.condition !==
                        get(n, ['filters'])?.[indexParent]?.value?.[1]
                          ?.condition
                      );
                    }}
                  >
                    {({ getFieldValue, setFieldValue }) => {
                      const currentFilters = getFieldValue('filters');
                      setFieldValue(
                        [
                          'filters',
                          indexParent,
                          'value',
                          subField.name,
                          'condition',
                        ],
                        currentFilters?.[indexParent]?.value?.[1]?.condition,
                      );

                      return (
                        <Form.Item
                          name={[subField.name, 'condition']}
                          rules={[
                            {
                              required: true,
                              message: 'Missing Root condition',
                            },
                          ]}
                          className="w-[80px]"
                        >
                          <Input disabled />
                        </Form.Item>
                      );
                    }}
                  </Form.Item>
                )}
              </>
            )}
          </>
        )}
      </div>
      <Form.Item
        {...props}
        name={[subField.name, 'key']}
        rules={[{ required: true, message: 'Missing key' }]}
      >
        <Select
          options={filters.map((e) => ({
            value: e.key,
            label: e.label,
          }))}
          className="min-w-[200px]"
          placeholder="Data"
          onSelect={() => {
            form.setFieldValue(
              ['filters', indexParent, 'value', subField?.name, 'value'],
              null,
            );
            form.setFieldValue(
              ['filters', indexParent, 'value', subField?.name, 'operator'],
              null,
            );
          }}
        />
      </Form.Item>
      <Form.Item
        {...props}
        name={[subField.name, 'operator']}
        rules={[{ required: true, message: 'Missing condition' }]}
      >
        {filter?.isDatePicker || filter?.isCreator || filter?.isCheckNull ? (
          <Input disabled className="max-w-[120px]" />
        ) : (
          <Select
            className="min-w-150"
            options={filter?.operators || OPERATORS}
            placeholder="Operator"
          />
        )}
      </Form.Item>
      <ValueForm
        filters={filters}
        extraProps={props}
        name={subField.name}
        indexParent={indexParent}
        form={form}
      />

      {!hiddenRemove && (
        <DeleteOutlined onClick={() => remove(subField.name)} />
      )}
    </Space>
  );
};

export default FilterRow;
